import React, { useMemo } from 'react';

function clamp(v, min, max) { return Math.min(max, Math.max(min, v)); }

// Helper function to format numbers with decimal and thousands separators
const formatNumber = (value, format) => {
  if (!format || typeof value !== 'number') return value?.toFixed?.(format?.decimals ?? 2) ?? value;

  const { decimals = 2, decimalSeparator = '.', thousandsSeparator = ',' } = format;

  // Round to specified decimals
  const rounded = Number(value.toFixed(decimals));

  // Split into integer and decimal parts
  const [intPart, decPart] = rounded.toFixed(decimals).split('.');

  // Add thousands separator to integer part
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

  // Combine with decimal part using specified separator
  return decPart ? `${formattedInt}${decimalSeparator}${decPart}` : formattedInt;
};

export default function RadialGauge({
  value = 0,
  min = 0,
  max = 100,
  label,
  unit = '',
  size = 120,
  numberFormat = null, // { decimals, decimalSeparator, thousandsSeparator }
  majorTicks = 5,
  minorTicks = 2,
  startAngle = -135, // degrees
  endAngle = 135,    // degrees
  greenZones = [],
  yellowZones = [],
  redZones = [],
  colors = { green: '#109618', yellow: '#FF9900', red: '#DC3912' },
  // Percentage-based zones
  normalZoneEnd = 70,     // Normal zone: 0-70%
  warningZoneEnd = 90,    // Warning zone: 70-90%
  // Alarm zone: 90-100%
  showZones = true,
  normalColor = '#4caf50',  // Normal zone color
  warningColor = '#ff9800', // Warning zone color
  dangerColor = '#f44336',  // Danger zone color
}) {
  const S = size;
  const cx = S / 2;
  const cy = S / 2;
  const radius = S * 0.97 / 2;
  const range = max - min || 1;

  const sweep = endAngle - startAngle;
  const angleFor = (val) => startAngle + ((val - min) / range) * sweep;
  const rad = (deg) => (deg * Math.PI) / 180;

  const polar = (angDeg, r) => ({
    x: cx + r * Math.cos(rad(angDeg)),
    y: cy + r * Math.sin(rad(angDeg)),
  });

  const ringPath = (startDeg, endDeg, rInner, rOuter) => {
    const a0 = rad(startDeg);
    const a1 = rad(endDeg);
    const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
    const p0o = polar(startDeg, rOuter);
    const p1o = polar(endDeg, rOuter);
    const p1i = polar(endDeg, rInner);
    const p0i = polar(startDeg, rInner);
    return [
      `M ${p0o.x} ${p0o.y}`,
      `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${p1o.x} ${p1o.y}`,
      `L ${p1i.x} ${p1i.y}`,
      `A ${rInner} ${rInner} 0 ${largeArc} 0 ${p0i.x} ${p0i.y}`,
      'Z',
    ].join(' ');
  };

  const tickElems = useMemo(() => {
    const elems = [];
    const majorDelta = range / (majorTicks - 1);
    const fontSize = Math.round(S / 16);
    for (let major = min; major <= max + 1e-6; major += majorDelta) {
      const minorDelta = majorDelta / minorTicks;
      for (let m = major + minorDelta; m < Math.min(major + majorDelta, max) - 1e-6; m += minorDelta) {
        const a = angleFor(m);
        const p1 = polar(a, radius * 0.85);
        const p2 = polar(a, radius * 0.75);
        elems.push(
          <line key={`mn-${m.toFixed(3)}`} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="#666" strokeWidth={1} />
        );
      }
      const aM = angleFor(major);
      const p1M = polar(aM, radius * 0.85);
      const p2M = polar(aM, radius * 0.7);
      elems.push(<line key={`mj-${major}`} x1={p1M.x} y1={p1M.y} x2={p2M.x} y2={p2M.y} stroke="#333" strokeWidth={2} />);
      if (Math.abs(major - min) < 1e-6 || Math.abs(major - max) < 1e-6) {
        const pLbl = polar(aM, radius * 0.63);
        elems.push(
          <text key={`lbl-${major}`} x={pLbl.x} y={pLbl.y + fontSize / 3} fontSize={fontSize} textAnchor={major === min ? 'start' : 'end'} fill="#333">{Math.round(major)}</text>
        );
      }
    }
    return elems;
  }, [S, range, majorTicks, minorTicks, min, max]);

  const bands = useMemo(() => {
    const make = (zones, color) => zones.map((z, idx) => {
      const a0 = angleFor(z.from);
      const a1 = angleFor(z.to);
      return <path key={`${color}-${idx}`} d={ringPath(a0, a1, radius * 0.65, radius * 0.85)} fill={color} />
    });
    
    // Create percentage-based zones if enabled
    const percentageZones = [];
    if (showZones) {
      // Normal zone (0 to normalZoneEnd%)
      const normalStart = min;
      const normalEnd = min + (range * normalZoneEnd / 100);
      if (normalEnd > normalStart) {
        const a0 = angleFor(normalStart);
        const a1 = angleFor(normalEnd);
        percentageZones.push(
          <path key="normal-zone" d={ringPath(a0, a1, radius * 0.9, radius * 0.97)} fill={`${normalColor}B3`} stroke={`${normalColor}E6`} strokeWidth="1" />
        );
      }
      
      // Warning zone (normalZoneEnd% to warningZoneEnd%)
      const warningStart = min + (range * normalZoneEnd / 100);
      const warningEnd = min + (range * warningZoneEnd / 100);
      if (warningEnd > warningStart) {
        const a0 = angleFor(warningStart);
        const a1 = angleFor(warningEnd);
        percentageZones.push(
          <path key="warning-zone" d={ringPath(a0, a1, radius * 0.9, radius * 0.97)} fill={`${warningColor}B3`} stroke={`${warningColor}E6`} strokeWidth="1" />
        );
      }
      
      // Alarm zone (warningZoneEnd% to 100%)
      const alarmStart = min + (range * warningZoneEnd / 100);
      const alarmEnd = max;
      if (alarmEnd > alarmStart) {
        const a0 = angleFor(alarmStart);
        const a1 = angleFor(alarmEnd);
        percentageZones.push(
          <path key="alarm-zone" d={ringPath(a0, a1, radius * 0.9, radius * 0.97)} fill={`${dangerColor}B3`} stroke={`${dangerColor}E6`} strokeWidth="1" />
        );
      }
    }
    
    return [
      ...percentageZones,
      ...make(greenZones || [], colors.green),
      ...make(yellowZones || [], colors.yellow),
      ...make(redZones || [], colors.red),
    ];
  }, [greenZones, yellowZones, redZones, colors, range, min, max, normalZoneEnd, warningZoneEnd, showZones]);

  const pointerPath = useMemo(() => {
    const v = clamp(value, min, max);
    const a = angleFor(v);
    const deltaAngle = (Math.abs(sweep) || 270) / 13;
    const a1 = a - deltaAngle;
    const a2 = a + deltaAngle;
    const head = polar(a, radius * 0.85);
    const head1 = polar(a1, radius * 0.12);
    const head2 = polar(a2, radius * 0.12);
    // Removed tail - just create a simple arrow pointer without opposite tail
    const points = [head, head1, head2, head];
    return 'M ' + points.map(p => `${p.x} ${p.y}`).join(' L ');
  }, [value, min, max]);

  const fontSizeLabel = Math.round(S / 9);
  const fontSizeValue = Math.round(S / 10);
  const vMid = clamp(value, min, max);

  return (
    <svg width={S} height={S} viewBox={`0 0 ${S} ${S}`} style={{ maxWidth: '100%', maxHeight: '100%' }}>
      <circle cx={cx} cy={cy} r={radius} fill="#ccc" stroke="#000" strokeWidth={0.5} />
      <circle cx={cx} cy={cy} r={radius * 0.9} fill="#fff" stroke="#e0e0e0" strokeWidth={2} />
      {bands}
      {label && (
        <text x={cx} y={cy / 2 + fontSizeLabel / 2} dy={fontSizeLabel / 2} textAnchor="middle" fontSize={fontSizeLabel} fill="#333">{label}</text>
      )}
      {tickElems}
      <g>
        <path d={pointerPath} fill="#dc3912" stroke="#c63310" fillOpacity={0.7} />
        <circle cx={cx} cy={cy} r={radius * 0.12} fill="#4684EE" stroke="#666" />
        <text x={cx} y={S - cy / 4 - fontSizeValue} dy={fontSizeValue / 2} textAnchor="middle" fontSize={fontSizeValue} fill="#000">
          {formatNumber(vMid, numberFormat)}{unit ? ` ${unit}` : ''}
        </text>
      </g>
    </svg>
  );
}
